import { useEffect, useState } from 'react';
import { Bluetooth, Check, Monitor, Printer, RefreshCw } from 'lucide-react';
import {
  agentPrinters,
  agentStatus,
  isAndroid,
  printDocument,
  printRoute,
  setAgentPrinter,
  setPrintRoute,
  type PrintRoute,
} from '@/lib/printing';
import { Alert, Button, Card, CardBody, CardHeader, Field, Input, Select } from '@/components/ui';

const ROUTES: { value: PrintRoute; label: string; icon: typeof Printer; detail: string }[] = [
  {
    value: 'auto',
    label: 'Automatic',
    icon: Check,
    detail: 'The driver if it is running, then RawBT on a phone, then the browser.',
  },
  {
    value: 'agent',
    label: 'Printer driver',
    icon: Printer,
    detail: 'A printer plugged into this computer, through the Stockpilot driver.',
  },
  {
    value: 'rawbt',
    label: 'Bluetooth (RawBT)',
    icon: Bluetooth,
    detail: 'A Bluetooth printer paired with this phone, through the RawBT app.',
  },
  {
    value: 'browser',
    label: "The browser's print dialog",
    icon: Monitor,
    detail: 'Whatever paper this device can already print on.',
  },
];

/** What a receipt looks like, for proving a printer works. */
const TEST_RECEIPT = {
  blocks: [
    { type: 'center' as const, text: 'TEST PRINT' },
    { type: 'rule' as const, char: '=' },
    { type: 'pair' as const, label: 'Printed', value: new Date().toLocaleString() },
    { type: 'item' as const, label: 'Sample item', sku: 'TEST-1', quantity: '2', price: '1,200', total: '2,400' },
    { type: 'rule' as const },
    { type: 'big' as const, label: 'TOTAL', text: '2,400' },
    { type: 'center' as const, text: 'If you can read this, printing works.' },
  ],
};

/**
 * How this device prints a receipt.
 *
 * A till, a phone and a laptop each reach a printer differently, and which one
 * this is is a property of the device rather than of the business — so the
 * choice is kept here, beside the other per-device settings, and never sent to
 * the server.
 */
export function PrinterCard() {
  const [route, setRoute] = useState<PrintRoute>(printRoute());
  const [status, setStatus] = useState<{ running: boolean; printer?: string } | null>(null);
  const [printers, setPrinters] = useState<string[]>([]);
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    setBusy(true);
    try {
      const found = await agentStatus();
      setStatus(found);
      setPrinters(found.running ? await agentPrinters() : []);
      if (found.printer) setChosen(found.printer);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void refresh();
    // Asked once on mount; the Check button is there for after something
    // changed on the machine itself.
  }, []);

  const choose = (next: PrintRoute): void => {
    setRoute(next);
    setPrintRoute(next);
    setNotice(null);
    setFailure(null);
  };

  const save = async (): Promise<void> => {
    setNotice(null);
    setFailure(null);
    setBusy(true);
    try {
      const result = await setAgentPrinter(chosen.trim());
      if (result.printed) {
        setNotice(`Receipts will print to ${chosen.trim()}.`);
        await refresh();
      } else {
        setFailure(result.error);
      }
    } finally {
      setBusy(false);
    }
  };

  const test = async (): Promise<void> => {
    setNotice(null);
    setFailure(null);
    const result = await printDocument(TEST_RECEIPT, route);
    if (result.printed) {
      setNotice(
        result.via === 'rawbt'
          ? 'Sent to RawBT. If nothing opened, install RawBT from Google Play.'
          : `Sent to the ${result.via === 'agent' ? 'printer driver' : 'browser'}.`,
      );
    } else {
      setFailure(result.error);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Receipt printing"
        description="How this device prints a receipt. Kept here, not on the server, so a phone at the counter and the office laptop can differ."
        action={
          <Button variant="ghost" icon={RefreshCw} loading={busy} onClick={() => void refresh()}>
            Check
          </Button>
        }
      />

      <CardBody className="space-y-5">
        {notice && <Alert tone="success">{notice}</Alert>}
        {failure && <Alert tone="danger">{failure}</Alert>}

        <Field label="Print through">
          <div className="grid gap-2 sm:grid-cols-2">
            {ROUTES.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={route === option.value}
                onClick={() => choose(option.value)}
                className={`flex items-start gap-3 rounded-xl border p-3 text-left ${
                  route === option.value ? 'border-brand-600' : 'border-subtle hover:bg-sunken'
                }`}
              >
                <option.icon className="mt-0.5 size-4 shrink-0 text-secondary" aria-hidden />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{option.label}</span>
                  <span className="block text-xs text-muted">{option.detail}</span>
                </span>
              </button>
            ))}
          </div>
        </Field>

        {(route === 'agent' || route === 'auto') && (
          <Field
            label="Printer on this computer"
            hint={
              status?.running
                ? 'Pick one the driver can see, or type the name exactly as the computer shows it.'
                : 'Start the Stockpilot printer driver on this computer to choose a printer here.'
            }
          >
            <div className="space-y-2">
              <p className="text-sm">
                {status === null ? (
                  <span className="text-muted">Looking for the driver…</span>
                ) : status.running ? (
                  <span className="text-secondary">
                    Driver running · printing to{' '}
                    <span className="font-medium">{status.printer ?? 'nothing yet'}</span>
                  </span>
                ) : (
                  <span className="text-muted">No driver running on this computer.</span>
                )}
              </p>

              {printers.length > 0 && (
                <Select
                  label="Printers this computer knows"
                  value={printers.includes(chosen) ? chosen : ''}
                  onChange={(event) => setChosen(event.target.value)}
                >
                  <option value="">Type a name instead</option>
                  {printers.map((printer) => (
                    <option key={printer} value={printer}>{printer}</option>
                  ))}
                </Select>
              )}

              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    label="Printer name"
                    placeholder="POS-58 Printer"
                    value={chosen}
                    disabled={!status?.running}
                    onChange={(event) => setChosen(event.target.value)}
                  />
                </div>
                <Button
                  variant="outline"
                  disabled={!status?.running || !chosen.trim()}
                  loading={busy}
                  onClick={() => void save()}
                >
                  Save
                </Button>
              </div>

              <p className="text-xs text-muted">
                On Windows this is the name in Settings → Printers &amp; scanners, spaces and all.
                On a Mac it is the CUPS queue, written <code>cups:NAME</code>. A network printer is{' '}
                <code>192.168.1.50:9100</code>.
              </p>
            </div>
          </Field>
        )}

        {(route === 'rawbt' || (route === 'auto' && isAndroid())) && (
          <Alert tone="info">
            RawBT is a free Android app that talks to a Bluetooth thermal printer. Install it,
            pair the printer in RawBT once, and receipts printed from this phone will open it with
            the receipt already loaded.
          </Alert>
        )}

        <div>
          <Button variant="outline" icon={Printer} onClick={() => void test()}>
            Print a test receipt
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
